import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";

interface JoinFamily {
  _id: string;
  heading: string;
  description: string;
  desktopImage: string;
  mobileImage: string;
}

export default function JoinFamily() {
  const [banner, setBanner] = useState<JoinFamily | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<JoinFamily | null>(null);

  // Fetch banners
  const fetchBanners = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/joinFamily/alldetails`
      );
      const selectedBanner = res.data.message[0]; // pick 3rd item
      setBanner(selectedBanner);
    } catch (error) {
      console.error("Error fetching banner:", error);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleEdit = (banner: JoinFamily) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  return (
    <div className="pt-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-lato-regular">Section - 14</h2>
        <hr />
      </div>

      {banner && (
        <div className="w-full bg-white border p-4 border-gray-200 rounded-xl overflow-hidden">
          {banner.desktopImage && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${banner.desktopImage}`}
              alt={banner.heading}
              className="w-full h-[18rem] xl:h-[24rem]  object-cover object-top"
            />
          )}

          <div className="mt-4">
          {banner.mobileImage && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${banner.mobileImage}`}
              alt={banner.heading}
              className="w-[24rem]  h-[12rem] object-contain object-top"
            />
          )}
          </div>

          <div className="p-5">
            <h3
              className=" font-semibold text-black font-lato-regular mb-2"
              dangerouslySetInnerHTML={{ __html: banner.heading }}
            />
            <h4 className="text-xl font-semibold text-black font-lato-regular mb-2">
              {banner.description}
            </h4>
            <div className="flex justify-start gap-3">
              <button
                className="bg-blue-500 font-lato-regular text-white px-4 py-2 rounded"
                onClick={() => handleEdit(banner)}
              >
                <span className="inline-block mr-1">
                  <Icon icon={FaRegEdit} />
                </span>
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && editingBanner && (
        <BannerFormModal
          onClose={() => setShowForm(false)}
          fetchBanners={fetchBanners}
          editingBanner={editingBanner}
        />
      )}
    </div>
  );
}

interface BannerFormModalProps {
  onClose: () => void;
  fetchBanners: () => void;
  editingBanner: JoinFamily; // your component type
}

const BannerFormModal = ({
  onClose,
  fetchBanners,
  editingBanner,
}: BannerFormModalProps) => {
  const [heading, setHeading] = useState(editingBanner.heading || "");
  const [description, setDescription] = useState(
    editingBanner.description || ""
  );
  const [imageFiledesktop, setImageFiledesktop] = useState<File | null>(null);
  const [imageFilemobile, setImageFilemobile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!heading.trim()) return;

    const formData = new FormData();
    formData.append("heading", heading);
    formData.append("description", description);

    if (imageFiledesktop) formData.append("desktopImage", imageFiledesktop);

    if (imageFilemobile) formData.append("mobileImage", imageFilemobile);

    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/joinFamily/update/${editingBanner._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      fetchBanners(); // refresh data
      onClose(); // close modal
    } catch (error) {
      console.error("Error updating banner:", error);
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Heading */}
          <label className="block font-lato-bold">Heading</label>
          <input
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Description */}
          <label className="block font-lato-bold">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2"
            rows={3}
          />

          {/* Image Preview */}
          {editingBanner.desktopImage && !imageFiledesktop && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${editingBanner.desktopImage}`}
              alt="banner"
              className="w-24 h-24 object-cover rounded-md mb-2"
            />
          )}
          <label className="block font-lato-bold">Upload Desktop Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFiledesktop(e.target.files?.[0] || null)}
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {editingBanner.mobileImage && !imageFilemobile && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${editingBanner.mobileImage}`}
              alt="banner"
              className="w-24 h-24 object-cover rounded-md mb-2"
            />
          )}
          <label className="block font-lato-bold">Upload Mobile Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFilemobile(e.target.files?.[0] || null)}
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
